import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "./button";
import { Card, CardContent } from "./card";
import SelectField from "./SelectField";
import InputField from "./InputField";
import TextAreaField from "./TextAreaField";

// ─── Utilidades de distribución ───────────────────────────────────────────────

/**
 * Agrupa los equipos respetando la jerarquía grupo → área.
 * Retorna: { [grupoId]: { grupoNombre, grupoId, areas: { [areaId]: { areaNombre, equipos[] } } } }
 */
function agruparEquiposPorJerarquia(selectedEquipos, selectedAreas) {
  const areaMap = {};
  selectedAreas.forEach((area) => {
    areaMap[String(area._id)] = area;
  });

  const grupos = {};
  selectedEquipos.forEach((equipo) => {
    const areaId = String(equipo.area?._id ?? equipo.area);
    const area = areaMap[areaId];
    if (!area) return;

    const grupoId = String(area.grupo?._id ?? area.grupo);
    const grupoNombre = area.grupo?.name ?? grupoId;

    if (!grupos[grupoId]) {
      grupos[grupoId] = { grupoNombre, grupoId, areas: {} };
    }
    if (!grupos[grupoId].areas[areaId]) {
      grupos[grupoId].areas[areaId] = { areaNombre: area.name, equipos: [] };
    }
    grupos[grupoId].areas[areaId].equipos.push(equipo);
  });

  return grupos;
}

function construirBloques(grupos) {
  const bloques = [];
  Object.values(grupos).forEach((grupo) => {
    Object.entries(grupo.areas).forEach(([areaId, area]) => {
      bloques.push({
        grupoNombre: grupo.grupoNombre,
        grupoId: grupo.grupoId,
        areaNombre: area.areaNombre,
        areaId,
        equipos: [...area.equipos],
      });
    });
  });
  return bloques;
}

function asignarBloques(bloques, responsables) {
  const asignaciones = responsables.map((p) => ({ persona: p, bloques: [] }));
  const totalEquipos = (asig) =>
    asig.bloques.reduce((sum, b) => sum + b.equipos.length, 0);

  if (bloques.length >= responsables.length) {
    const sorted = [...bloques].sort((a, b) => b.equipos.length - a.equipos.length);
    sorted.forEach((bloque) => {
      const target = asignaciones.reduce((min, curr) =>
        totalEquipos(curr) < totalEquipos(min) ? curr : min,
      );
      target.bloques.push({ ...bloque, equipos: [...bloque.equipos] });
    });
  } else {
    const equiposAnotados = bloques.flatMap((b) =>
      b.equipos.map((e) => ({
        equipo: e,
        grupoNombre: b.grupoNombre,
        grupoId: b.grupoId,
        areaNombre: b.areaNombre,
        areaId: b.areaId,
      })),
    );

    const total = equiposAnotados.length;
    const base = Math.floor(total / responsables.length);
    const sobrantes = total % responsables.length;
    let idx = 0;

    asignaciones.forEach((asig, i) => {
      const cantidad = base + (i < sobrantes ? 1 : 0);
      const slice = equiposAnotados.slice(idx, idx + cantidad);
      idx += cantidad;

      const bloquesMap = {};
      slice.forEach(({ equipo, grupoNombre, grupoId, areaNombre, areaId }) => {
        const key = `${grupoId}__${areaId}`;
        if (!bloquesMap[key]) bloquesMap[key] = { grupoNombre, areaNombre, equipos: [] };
        bloquesMap[key].equipos.push(equipo);
      });
      asig.bloques = Object.values(bloquesMap);
    });
  }

  return asignaciones;
}

/**
 * Genera plan de distribución: una entrada por responsable con sus equipos asignados.
 * Retorna: [{ personaId, personaNombre, equipoIds[], bloques[] }]
 */
function generarPlanAsignacion(selectedEquipos, selectedPersonas, selectedAreas) {
  const grupos = agruparEquiposPorJerarquia(selectedEquipos, selectedAreas);
  const bloques = construirBloques(grupos);
  const asignaciones = asignarBloques(bloques, selectedPersonas);

  return asignaciones.map(({ persona, bloques: bloquesPersona }) => ({
    personaId: persona._id,
    personaNombre: persona.name,
    equipoIds: bloquesPersona.flatMap((b) => b.equipos.map((e) => e._id ?? e)),
    bloques: bloquesPersona,
  }));
}

// ─── Sub-componente: Previsualización de asignación ───────────────────────────

function AsignacionPreviewPanel({ plan, estadisticas }) {
  const [abiertos, setAbiertos] = useState({});

  const toggle = (key) =>
    setAbiertos((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <div className="border border-indigo-200 bg-indigo-50/30 rounded-xl p-4 space-y-3">
      {/* Encabezado */}
      <div className="flex flex-wrap justify-between items-center gap-2">
        <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
          Vista previa de la asignación
        </p>
        <span className="text-xs text-gray-500">
          {plan.length} responsable(s) · {estadisticas?.totalEquipos ?? 0} equipo(s)
        </span>
      </div>

      {/* Estadísticas */}
      {estadisticas && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {[
            { label: "Equipos", value: estadisticas.totalEquipos },
            { label: "Responsables", value: estadisticas.totalResponsables },
            { label: "Máx. por resp.", value: estadisticas.maxPorResponsable },
          ].map(({ label, value }) => (
            <div
              key={label}
              className="bg-white border border-indigo-100 rounded-lg p-2 text-center"
            >
              <p className="text-base font-bold text-indigo-700">{value}</p>
              <p className="text-xs text-gray-500">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Acordeón por responsable */}
      <div className="space-y-1 max-h-72 overflow-y-auto pr-1">
        {plan.map((entrada) => {
          const key = entrada.personaId ?? entrada.personaNombre;
          const abierto = abiertos[key] ?? false;

          return (
            <div
              key={key}
              className="border border-indigo-200 rounded-lg overflow-hidden bg-white"
            >
              <button
                type="button"
                onClick={() => toggle(key)}
                className="w-full flex justify-between items-center px-3 py-2 text-left hover:bg-indigo-50 transition-colors"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-indigo-500 text-xs font-bold shrink-0" aria-hidden>
                    {abierto ? "▼" : "▶"}
                  </span>
                  <span className="text-sm font-semibold text-gray-800 truncate">
                    {entrada.personaNombre}
                  </span>
                </div>
                <span className="text-xs font-semibold text-indigo-700 shrink-0 ml-2 bg-indigo-100 px-2 py-0.5 rounded-full">
                  {entrada.equipoIds.length} eq.
                </span>
              </button>

              {abierto && (
                <div className="border-t border-indigo-100 px-4 pb-3 pt-2 space-y-2 bg-gray-50/50">
                  {entrada.bloques.map((bloque, bi) => (
                    <div key={bi}>
                      <p className="text-xs font-semibold text-gray-600 mb-1">
                        <span className="text-blue-600">{bloque.grupoNombre}</span>
                        <span className="text-gray-400 mx-1">/</span>
                        <span>{bloque.areaNombre}</span>
                        <span className="text-gray-400 ml-1 font-normal">
                          ({bloque.equipos.length})
                        </span>
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {bloque.equipos.map((eq) => (
                          <span
                            key={eq._id ?? String(eq)}
                            className="inline-block bg-white border border-gray-200 rounded px-1.5 py-0.5 text-xs text-gray-600 font-mono"
                          >
                            {eq.codigo ?? eq._id ?? String(eq)}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

const AsignarPersonaForm = ({ onClose, onSave }) => {
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState("");

  const [sedes, setSedes] = useState([]);
  const [selectedSede, setSelectedSede] = useState("");

  const [areas, setAreas] = useState([]);
  const [selectedAreas, setSelectedAreas] = useState([]);

  const [equipos, setEquipos] = useState([]);
  const [selectedEquipos, setSelectedEquipos] = useState([]);

  const [personas, setPersonas] = useState([]);
  const [selectedPersona, setSelectedPersona] = useState(""); // modo simple
  const [selectedPersonas, setSelectedPersonas] = useState([]); // modo múltiple

  const [observaciones, setObservaciones] = useState("");
  const [loading, setLoading] = useState(false);

  // ── Estados para modo múltiple ──
  const [modoMultiple, setModoMultiple] = useState(false);
  const [fechaInicio, setFechaInicio] = useState("");
  const [fechaFin, setFechaFin] = useState("");
  const [planAsignacion, setPlanAsignacion] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  const token = localStorage.getItem("token");
  const API = process.env.REACT_APP_API_URL;

  // ── Selección rápida ──
  const seleccionarTodosEquipos = () => setSelectedEquipos(equipos);
  const limpiarEquipos = () => setSelectedEquipos([]);
  const seleccionarTodasAreas = () => setSelectedAreas(areas);
  const limpiarAreas = () => setSelectedAreas([]);

  // Invalidar plan cuando cambian inputs que lo afectan
  useEffect(() => {
    setPlanAsignacion(null);
    setShowPreview(false);
  }, [selectedEquipos, selectedPersonas]);

  // ─── Carga de datos ──────────────────────────────────────────────────────

  useEffect(() => {
    axios
      .get(`${API}/clientes`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setClientes(res.data))
      .catch(() => toast.error("Error al cargar clientes"));
  }, [API, token]);

  useEffect(() => {
    if (!selectedCliente) {
      setSedes([]);
      setSelectedSede("");
      setAreas([]);
      setSelectedAreas([]);
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }
    setSelectedSede("");
    setSelectedAreas([]);
    setEquipos([]);
    setSelectedEquipos([]);
    axios
      .get(`${API}/sedes/${selectedCliente}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => setSedes(res.data || []))
      .catch(() => toast.error("Error al cargar sedes"));
  }, [selectedCliente, API, token]);

  useEffect(() => {
    if (!selectedCliente) {
      setAreas([]);
      setSelectedAreas([]);
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }
    axios
      .get(
        `${API}/areas/con_equipos?cliente=${selectedCliente}&sede=${selectedSede || "all"}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => setAreas(res.data))
      .catch(() => toast.error("Error al cargar áreas"));
  }, [selectedCliente, selectedSede, API, token]);

  useEffect(() => {
    setSelectedAreas([]);
    setEquipos([]);
    setSelectedEquipos([]);
  }, [selectedSede]);

  useEffect(() => {
    const areaIds = selectedAreas.map((a) => a?._id ?? a).filter(Boolean);
    if (!areaIds.length) {
      setEquipos([]);
      setSelectedEquipos([]);
      return;
    }
    const sedeQuery = selectedSede ? `&sede=${selectedSede}` : "";
    axios
      .get(
        `${API}/equipos?area=${areaIds.join(",")}&conCronograma=pendiente${sedeQuery}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => setEquipos(res.data))
      .catch(() => toast.error("Error al cargar equipos programados"));
  }, [selectedAreas, selectedSede, API, token]);

  useEffect(() => {
    axios
      .get(`${API}/users/resp`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setPersonas(res.data))
      .catch(() => toast.error("Error al cargar personas"));
  }, [API, token]);

  // ─── Estadísticas en tiempo real (modo múltiple) ─────────────────────────

  const estadisticasAsignacion = useMemo(() => {
    if (!modoMultiple || !selectedEquipos.length || !selectedPersonas.length)
      return null;

    const totalEquipos = selectedEquipos.length;
    const totalResponsables = selectedPersonas.length;
    const base = Math.floor(totalEquipos / totalResponsables);
    const maxPorResponsable = base + (totalEquipos % totalResponsables > 0 ? 1 : 0);

    return { totalEquipos, totalResponsables, maxPorResponsable };
  }, [modoMultiple, selectedEquipos, selectedPersonas]);

  // ─── Generar plan ────────────────────────────────────────────────────────

  const handleGenerarPlan = () => {
    if (!selectedEquipos.length)
      return toast.warning("Seleccione al menos un equipo");
    if (!selectedPersonas.length)
      return toast.warning("Seleccione al menos un responsable");

    const plan = generarPlanAsignacion(selectedEquipos, selectedPersonas, selectedAreas);
    setPlanAsignacion(plan);
    setShowPreview(true);
  };

  // ─── Validación ──────────────────────────────────────────────────────────

  const validate = () => {
    if (!selectedCliente) return toast.warning("Seleccione un cliente");
    if (!selectedAreas.length) return toast.warning("Seleccione al menos un área");
    if (!selectedEquipos.length) return toast.warning("Seleccione al menos un equipo");

    if (modoMultiple) {
      if (!selectedPersonas.length) return toast.warning("Seleccione al menos un responsable");
      if (!planAsignacion) return toast.warning("Genere el plan de asignación antes de guardar");
    } else {
      if (!selectedPersona) return toast.warning("Seleccione una persona");
    }

    return true;
  };

  // ─── Submit ───────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    if (!validate()) return;
    setLoading(true);

    const areaIds = selectedAreas.map((a) => a?._id ?? a).filter(Boolean);
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (modoMultiple) {
        // Una llamada por responsable con sus equipos asignados
        for (const entrada of planAsignacion) {
          await axios.post(
            `${API}/cronograma/asignar`,
            {
              clienteId: selectedCliente,
              areaIds,
              equipoIds: entrada.equipoIds,
              personaId: entrada.personaId,
              ...(fechaInicio && { fechaInicio }),
              ...(fechaFin && { fechaFin }),
            },
            { headers },
          );
        }
      } else {
        await axios.post(
          `${API}/cronograma/asignar`,
          {
            clienteId: selectedCliente,
            areaIds,
            equipoIds: selectedEquipos.map((e) => e?._id ?? e).filter(Boolean),
            personaId: selectedPersona,
            ...(fechaInicio && { fechaInicio }),
            ...(fechaFin && { fechaFin }),
          },
          { headers },
        );
      }

      toast.success("Persona(s) asignada(s) correctamente");
      onSave();
      onClose();
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Error al asignar persona",
      );
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4">
      <div className="bg-white rounded-2xl shadow-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <Card className="border-none shadow-none">
          <CardContent className="p-6 space-y-4">

            {/* Header */}
            <div className="flex justify-between items-center mb-2">
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Asignar persona a servicios
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">
                  Solo se muestran equipos con mantenimientos programados
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
              >
                ✕
              </button>
            </div>

            {/* ── Campos base ── */}
            <SelectField
              label="Cliente *"
              name="cliente"
              value={selectedCliente}
              options={clientes}
              valueKey="_id"
              displayFn={(c) => c.name}
              placeholder="Seleccione cliente"
              onChange={(e) => setSelectedCliente(e.target.value)}
            />

            <SelectField
              label="Sede"
              name="sede"
              value={selectedSede}
              options={sedes}
              valueKey="_id"
              displayFn={(s) => s.name}
              placeholder={selectedCliente ? "Todas las sedes" : "Seleccione un cliente"}
              onChange={(e) => setSelectedSede(e.target.value)}
            />

            {/* Área multi-select */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium">Área *</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={seleccionarTodasAreas}
                    disabled={!areas.length}
                    className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
                  >
                    Seleccionar todas
                  </button>
                  <button
                    type="button"
                    onClick={limpiarAreas}
                    disabled={!selectedAreas.length}
                    className="text-xs text-red-500 hover:underline disabled:text-gray-400"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
              <SelectField
                name="area"
                value={
                  Array.isArray(selectedAreas)
                    ? selectedAreas.map((a) => a._id)
                    : []
                }
                options={areas}
                valueKey="_id"
                displayFn={(a) => `${a.grupo?.name ?? ""} - ${a.name}`}
                placeholder="Seleccione una o varias áreas"
                isMulti
                onChange={(e) => setSelectedAreas(e.target.value)}
              />
              {areas.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedAreas.length} de {areas.length} áreas seleccionadas
                </p>
              )}
            </div>

            {/* Equipos multi-select */}
            <div>
              <div className="flex justify-between items-center mb-1">
                <label className="block text-sm font-medium">
                  Equipos programados *
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={seleccionarTodosEquipos}
                    disabled={!equipos.length}
                    className="text-xs text-blue-600 hover:underline disabled:text-gray-400"
                  >
                    Seleccionar todos
                  </button>
                  <button
                    type="button"
                    onClick={limpiarEquipos}
                    disabled={!selectedEquipos.length}
                    className="text-xs text-red-500 hover:underline disabled:text-gray-400"
                  >
                    Limpiar
                  </button>
                </div>
              </div>
              <SelectField
                name="equipos"
                value={
                  Array.isArray(selectedEquipos)
                    ? selectedEquipos.map((e) => e._id)
                    : []
                }
                options={equipos}
                valueKey="_id"
                displayFn={(e) =>
                  `${e.marca?.name ?? ""} ${e.modelo?.name ?? ""} [${e.codigo}]`
                }
                placeholder={
                  selectedAreas.length
                    ? equipos.length
                      ? "Seleccione equipos"
                      : "Sin equipos programados en estas áreas"
                    : "Seleccione un área primero"
                }
                isMulti
                onChange={(e) => setSelectedEquipos(e.target.value)}
              />
              {equipos.length > 0 && (
                <p className="text-xs text-gray-500 mt-1">
                  {selectedEquipos.length} de {equipos.length} equipos
                  seleccionados
                </p>
              )}
            </div>

            {/* ── Toggle: modo múltiple ── */}
            <div className="flex items-center gap-2 pt-1">
              <input
                id="modoMultiple"
                type="checkbox"
                checked={modoMultiple}
                onChange={(e) => {
                  setModoMultiple(e.target.checked);
                  setPlanAsignacion(null);
                  setShowPreview(false);
                }}
                className="w-4 h-4 cursor-pointer accent-indigo-600"
              />
              <label
                htmlFor="modoMultiple"
                className="text-sm font-medium cursor-pointer select-none"
              >
                Distribuir entre múltiples responsables
              </label>
            </div>

            {/* ── Modo simple: un responsable ── */}
            {!modoMultiple && (
              <SelectField
                label="Responsable *"
                name="persona"
                value={selectedPersona}
                options={personas}
                valueKey="_id"
                displayFn={(p) => p.name}
                placeholder="Seleccione persona"
                onChange={(e) => setSelectedPersona(e.target.value)}
              />
            )}

            {/* ── Modo múltiple: distribución inteligente ── */}
            {modoMultiple && (
              <div className="border border-indigo-200 bg-indigo-50/40 rounded-xl p-4 space-y-3 w-full min-w-0">
                <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                  Distribución entre responsables
                </p>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Responsables *
                  </label>
                  <SelectField
                    name="personas"
                    value={
                      Array.isArray(selectedPersonas)
                        ? selectedPersonas.map((p) => p._id)
                        : []
                    }
                    options={personas}
                    valueKey="_id"
                    displayFn={(p) => p.name}
                    placeholder="Seleccione uno o varios responsables"
                    isMulti
                    onChange={(e) => setSelectedPersonas(e.target.value)}
                  />
                  {selectedPersonas.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {selectedPersonas.length} responsable(s) seleccionado(s)
                    </p>
                  )}
                </div>

                {/* Filtro de rango de fechas (opcional) */}
                <p className="text-xs text-gray-500">
                  Filtra qué mantenimientos programados se asignan (opcional)
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <InputField
                    label="Desde (fecha)"
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                  />
                  <InputField
                    label="Hasta (fecha)"
                    type="date"
                    value={fechaFin}
                    onChange={(e) => setFechaFin(e.target.value)}
                  />
                </div>

                {/* Estadísticas en tiempo real */}
                {estadisticasAsignacion && (
                  <div className="grid grid-cols-3 gap-2 pt-1">
                    {[
                      { label: "Equipos", value: estadisticasAsignacion.totalEquipos },
                      { label: "Responsables", value: estadisticasAsignacion.totalResponsables },
                      { label: "Máx. por resp.", value: estadisticasAsignacion.maxPorResponsable },
                    ].map(({ label, value }) => (
                      <div
                        key={label}
                        className="bg-white border border-indigo-100 rounded-lg p-2 text-center"
                      >
                        <p className="text-lg font-bold text-indigo-700">{value}</p>
                        <p className="text-xs text-gray-500">{label}</p>
                      </div>
                    ))}
                  </div>
                )}

                <Button
                  type="button"
                  variant="outline"
                  onClick={handleGenerarPlan}
                  disabled={!selectedEquipos.length || !selectedPersonas.length}
                  className="w-full border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                >
                  Previsualizar distribución
                </Button>
              </div>
            )}

            {/* ── Panel de previsualización ── */}
            {showPreview && planAsignacion && (
              <AsignacionPreviewPanel
                plan={planAsignacion}
                estadisticas={estadisticasAsignacion}
              />
            )}

            {/* Filtro de fechas en modo simple (opcional) */}
            {!modoMultiple && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <InputField
                  label="Desde (fecha, opcional)"
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
                <InputField
                  label="Hasta (fecha, opcional)"
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </div>
            )}

            <TextAreaField
              label="Observaciones"
              value={observaciones}
              onChange={(e) => setObservaciones(e.target.value)}
            />

            {/* Botones */}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  loading ||
                  !selectedEquipos.length ||
                  (modoMultiple && !planAsignacion)
                }
              >
                {loading ? "Asignando..." : "Asignar"}
              </Button>
            </div>

          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AsignarPersonaForm;
